import { Box, Typography, TextField as MuiTextField, InputAdornment } from "@mui/material";
import React, { useEffect, useState } from "react";
import FilterChoices from "../filter_choices/filter_choices";
import { useQuery } from "react-query";
import {
  downloadGetChoiceResponseList,
  getFamilyFilterData,
  getMasterData,
  getSubmittedChoices,
} from "../../../../services/choices/choices_services";
import XlsIcon from "../../../../assets/xls_icon.svg";
import FilterIcon from "../../../../assets/filter_icon.svg";
import SortIcon from "../../../../assets/sort.svg";
import { useParams } from "react-router-dom";
import { EnquiryLabels } from "../../../schemes/enquiries/enquiries_list";
import DataGridFamilyChoicesResponses from "../data_grid_family_responses/data_grid_family_responses";
import { downloadFromBlob } from "../../../../utils/utils";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../../../constants";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import styled from "@emotion/styled";
import { SearchRounded } from "@mui/icons-material";
import { useActiveResponseFilterCount, useSurveysResponseFilterStore } from "../../../../store/use-surveys-response-filter";
import { ToolbarButton } from "../../../../components/toolbar_button/toolbar_button";
import { purple } from "../../../../theme/colors";
import SortSurveysPopup, {
  DEFAULT_SURVEYS_SORT_OPTION,
  SurveysSortOption,
  surveysSortOptionLabel,
  surveysSortOptionToSortOrder,
} from "../sort_surveys_popup/sort_surveys_popup";
import { useSelectedOrgStore } from "../../../../store/use-selected-org-store";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

interface FamilyResponse {
  // choicesResponse: { total: number; list: SubmittedChoices[] };
  currentTab: number;
}

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function FamilyResponseList({
  // choicesResponse,
  currentTab,
}: FamilyResponse) {
  const { id } = useParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const [sortOption, setSortOption] = useState<SurveysSortOption>(DEFAULT_SURVEYS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);

  const { data: masterDataList } = useQuery(
    ["Family-filter-DataList"],
    async () => {
      let response = await getFamilyFilterData();
      return response.data.data;
    }
  );

  const { showSnackbar } = useFeedback();
  const { setOpenChoicesModal, setIncome, setEducation, setGenderList, setStates, setOccupation, setSearchValue } = useSurveysResponseFilterStore()
  const { ageFrom, ageTo, startDate, endDate, educationId, incomeId, occupationId, stateId, selectedGender, familyIncomeId, searchValue } = useSurveysResponseFilterStore()
  const activeFilterCount = useActiveResponseFilterCount()
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: choiceResponses, isLoading } = useQuery(
    // All filter-dialog values are part of the key (not just an
    // applyFilter/enabled gate) so react-query refetches automatically
    // whenever any of them actually changes. The old enabled:applyFilter
    // gate relied on setApplyFilter(false) running inside the query
    // function to reset itself - but if react-query ever decided cached
    // data for this key was still fresh and skipped the fetch entirely
    // (e.g. remounting after returning from View Response Details with
    // nothing actually changed), that reset never ran, permanently
    // stranding the grid in its loading state despite having valid data.
    [
      "family-choices-responses",
      id,
      searchValue,
      page,
      pageSize,
      sortOption,
      selectedOrgId,
      ageFrom,
      ageTo,
      startDate,
      endDate,
      educationId,
      incomeId,
      occupationId,
      stateId,
      selectedGender,
      familyIncomeId,
    ],
    async () => {
      let response = await getSubmittedChoices({
        type: currentTab === 0 ? "user" : "family",
        orgId: selectedOrgId,
        page: page + 1,
        size: pageSize,
        currentIncome: incomeId,
        dateFrom: startDate,
        dateTo: endDate,
        education: educationId,
        familyIncome: familyIncomeId,
        fromAge: ageFrom,
        toAge: ageTo,
        gender: selectedGender,
        occupation: occupationId,
        state: stateId,
        choiceId: +id!,
        sortOrder: surveysSortOptionToSortOrder(sortOption),
        keyword: searchValue,
      });
      return response?.data?.data?.data || [];
    }
  );

  useEffect(() => {
    if (masterDataList?.gender) {
      setGenderList(masterDataList.gender)
    }
    if (masterDataList?.personalIncome) {
      setIncome(masterDataList.personalIncome)
    }
    if (masterDataList?.state) {
      setStates(masterDataList.state)
    }
    if (masterDataList?.education) {
      setEducation(masterDataList.education)
    }
    if (masterDataList?.occupation) {
      setOccupation(masterDataList.occupation)
    }

  }, [masterDataList])

  function downloadEnquiryApiCall() {
    if (choiceResponses?.total <= 1000) {
      downloadGetChoiceResponseList({
        page: 1,
        size: 1000,
        type: currentTab === 0 ? "user" : "family",
        orgId: selectedOrgId,
        currentIncome: incomeId,
        dateFrom: startDate,
        dateTo: endDate,
        education: educationId,
        familyIncome: incomeId,
        fromAge: ageFrom,
        toAge: ageTo,
        gender: selectedGender,
        occupation: occupationId,
        state: stateId,
        format: "excel",
        choiceId: +id!,
        sortOrder: surveysSortOptionToSortOrder(sortOption)
      })
        .then((res) => {
          let extension = ".xlsx";
          downloadFromBlob(res, extension);
        })
        .catch((e) => { });
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  let labels: EnquiryLabels[] = [
    {
      label: "UUID",
    },
    {
      label: "Survey name",
    },
    {
      label: "Primary User Name",
    },
    {
      label: "Primary User Contact Number",
    },
    {
      label: "Family Member Name",
    },
    {
      label: "Relationship with Primary User",
    },
    {
      label: "Submitted On",
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        p={2}
      >
        <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
          <TextField
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size="small"
            fullWidth
            placeholder="Search Family Responses"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: "text.primary" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <ToolbarButton icon={XlsIcon} label="Export" onClick={downloadEnquiryApiCall} />
          <ToolbarButton
            icon={SortIcon}
            label={surveysSortOptionLabel(sortOption)}
            onClick={() => setSortDialogOpen(true)}
          />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={activeFilterCount}
            onClick={() => setOpenChoicesModal(true)}
          />
        </Box>
      </Box>
      <DataGridFamilyChoicesResponses
        labels={labels}
        count={choiceResponses?.total || 0}
        data={choiceResponses?.list || []}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isLoading={isLoading}
      />

      <SortSurveysPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
      <FilterChoices />
    </>
  );
}
